/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Feature } from 'geojson';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  useSetupDialog,
  uiTypes,
  uiActions,
  uiSelectors,
  layerActions,
  layerUtils,
  mapSelectors,
} from '@opengeoweb/store';
import SelectedFeaturesDialog from './SelectedFeaturesDialog';

export const SelectedFeaturesDialogConnect: React.FC = () => {
  const dispatch = useDispatch();

  const { dialogOrder, setDialogOrder, onCloseDialog, isDialogOpen, uiSource } =
    useSetupDialog(uiTypes.DialogTypes.SelectedFeatures);

  const activeMapId = useSelector((store: CoreAppStore) =>
    uiSelectors.getActiveWindowId(store),
  );

  const featureLayers = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapFeatureLayers(store, activeMapId),
  );

  const selectedFeatureLayers = useMemo(() => {
    return featureLayers.filter(
      (layer) =>
        layer.isSelectable && (layer.selectedFeatureIds?.length ?? 0) > 0,
    );
  }, [featureLayers]);

  const selectedFeatures = useMemo(() => {
    const featuresMap: Record<string, Feature> = {};
    selectedFeatureLayers.forEach((layer) => {
      const selectedFeatureIdSet = new Set(layer.selectedFeatureIds ?? []);
      const feature = layer.geojson?.features.find((candidate) => {
        const featureId = layerUtils.getFeatureSelectionKey(candidate);
        return featureId ? selectedFeatureIdSet.has(featureId) : false;
      });
      if (feature && layer.name) {
        featuresMap[layer.name] = feature;
      }
    });
    return featuresMap;
  }, [selectedFeatureLayers]);

  // Deselect the selected features and close the dialog
  const closeDialog = useCallback(() => {
    selectedFeatureLayers.forEach((layer) => {
      const selectedFeatureIdSet = new Set(layer.selectedFeatureIds ?? []);
      const selectedFeatureIndex = layer.geojson?.features.findIndex(
        (candidate) => {
          const featureId = layerUtils.getFeatureSelectionKey(candidate);
          return featureId ? selectedFeatureIdSet.has(featureId) : false;
        },
      );

      dispatch(
        layerActions.setSelectedFeatures({
          layerId: layer.id!,
          selectedFeatureIds: [],
        }),
      );
      if (selectedFeatureIndex !== undefined && selectedFeatureIndex >= 0) {
        dispatch(
          layerActions.updateFeatureProperties({
            layerId: layer.id!,
            properties: {
              highlightSelectedFeature: false,
            },
            targetFeatureIndex: selectedFeatureIndex,
          }),
        );
      }
    });
    onCloseDialog();
  }, [dispatch, onCloseDialog, selectedFeatureLayers]);

  useEffect(() => {
    if (
      Object.entries(selectedFeatures).length > 0 &&
      activeMapId !== undefined
    ) {
      dispatch(
        uiActions.setActiveMapIdForDialog({
          type: uiTypes.DialogTypes.SelectedFeatures,
          mapId: activeMapId,
          setOpen: true,
          source: 'app',
        }),
      );
    } else {
      onCloseDialog();
    }
  }, [activeMapId, onCloseDialog, dispatch, selectedFeatures]);

  return React.useMemo(() => {
    return (
      <SelectedFeaturesDialog
        features={selectedFeatures}
        isOpen={isDialogOpen}
        onClose={closeDialog}
        onMouseDown={setDialogOrder}
        order={dialogOrder}
        source={uiSource}
        startPosition={{ top: 85, left: 50 }}
      />
    );
  }, [
    dialogOrder,
    isDialogOpen,
    closeDialog,
    setDialogOrder,
    uiSource,
    selectedFeatures,
  ]);
};
